import { resolveSafePostLoginPath } from './postLoginRedirect';

const JWT_PARTS = 3;
/** Cognito custom:user_ulid values vary (ULID/UUID-like); allow safe identifier chars only. */
const SAFE_USER_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

function isLikelyJwt(token) {
    return typeof token === 'string' && token.split('.').length === JWT_PARTS && token.length > 20;
}

function readUserUlid(cognitoUser, idPayload) {
    const fromAttributes = cognitoUser?.attributes?.['custom:user_ulid'];
    const fromToken = idPayload?.['custom:user_ulid'];
    const userUlid = typeof fromAttributes === 'string' ? fromAttributes : fromToken;
    if (typeof userUlid !== 'string') return null;
    const trimmed = userUlid.trim();
    if (!SAFE_USER_ID_PATTERN.test(trimmed)) return null;
    return trimmed;
}

/**
 * Persist Amplify session tokens and redirect after a successful Cognito login.
 *
 * Security notes:
 * - Does NOT bypass MFA. Cognito must already have issued a valid session.
 * - Tokens are only taken from Amplify's CognitoUser.signInUserSession.
 * - Redirect targets are sanitized via resolveSafePostLoginPath.
 * - Requires custom:user_ulid so incomplete/forged sessions cannot proceed into the app.
 *
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function completeAmplifyLogin(cognitoUser, { onLogin , defaultPath='/dashboard'} = {}) {
    const session = cognitoUser?.signInUserSession;
    if (!session) {
        return { ok: false, reason: 'missing_session' };
    }

    const accessToken = session.accessToken?.jwtToken;
    const idToken = session.idToken?.jwtToken;
    const refreshToken = session.refreshToken?.token;

    if (!isLikelyJwt(accessToken) || !isLikelyJwt(idToken) || typeof refreshToken !== 'string' || !refreshToken) {
        return { ok: false, reason: 'invalid_tokens' };
    }

    const idPayload = session.idToken?.payload || {};
    const userUlid = readUserUlid(cognitoUser, idPayload);
    if (!userUlid) {
        return { ok: false, reason: 'missing_user_ulid' };
    }

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('idToken', idToken);
    localStorage.setItem('user_ulid', userUlid);

    if (typeof onLogin === 'function') {
        onLogin();
    }

    const fallback = resolveSafePostLoginPath(defaultPath) || '/dashboard';
    let nextPath = fallback;
    try {
        const raw = sessionStorage.getItem('postLoginRedirect');
        const safe = resolveSafePostLoginPath(raw);
        if (safe) {
            sessionStorage.removeItem('postLoginRedirect');
            const isBareDashboard = safe === '/dashboard' || safe === '/dashboard/';
            nextPath = isBareDashboard && fallback !== '/dashboard' ? fallback : safe;
        } else if (raw) {
            sessionStorage.removeItem('postLoginRedirect');
        }
    } catch {
        // ignore storage errors
    }

    window.location.assign(nextPath);
    return { ok: true };
}
